import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useCasbin, ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { useUserContext } from '@/modules/user/useUserContext';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import { ClaimsLogic, CLAIMS_LEVEL } from '@/modules/idv';
import PhoneVerificationModal from '@/modules/idv/PhoneVerificationModal';
import {
  MePrFoService,
  type InvitePreview,
  type ReceivedUpdateLike,
} from '@/services/meprfo';
import { TOPIC_TYPES } from '../types';
import type { Topic } from '../types';
import type { TopicViewMode } from '../TopicView';
import DefaultWelcomeInterstitial from '../WelcomeInterstitial';
import PrivateInviteInterstitial from '../PrivateInviteInterstitial';
import type { TopicWelcomeProps } from '../welcome/types';
import type { ReshareUpdatesStart } from '@/modules/share/session_storage';

interface UseTopicReadInterstitialsArgs {
  topicId?: string;
  topic: Topic | null;
  loading: boolean;
  /** Always 'view' from the component-driven path; TopicView passes its mode. */
  initialMode: TopicViewMode;
  /** Re-fetch the topic once a verification unlocks it (= fetchTopicInfo). */
  onTopicBecameReadable: () => void;
  /**
   * The welcome sheet to open. The garden read page dispatches the loaded
   * type's own `<type>/WelcomeInterstitial`; the classic view passes none and
   * gets the standard sheet, the same for every type.
   */
  WelcomeComponent?: React.FC<TopicWelcomeProps>;
}

/**
 * The start that passes a received update on: a reshare of the update leg,
 * carrying its summary unchanged. Null when there is no update, or it has no
 * summary, since an update never goes out without one.
 */
export function reshareOfReceivedUpdate(
  received: ReceivedUpdateLike | null
): ReshareUpdatesStart | null {
  const summary = received?.updateSummary?.trim();
  if (!received || !summary) return null;
  return {
    reshare: { previousLegId: received.legId },
    update: {
      // The leg's send time stands in when the original sender stored no
      // change-list head.
      aboutChangesSince: received.aboutChangesSince ?? received.sentAt,
      updateSummary: received.updateSummary,
    },
  };
}

export interface UseTopicReadInterstitials {
  /** The masked preview for a by-invitation-only topic, or null. */
  invitePreviewData: InvitePreview | null;
  /** True once private-invite detection has resolved (drives spinner vs card). */
  privateInviteResolved: boolean;
  /** Jump to the Profile Wizard to link/verify a phone for a private invite. */
  goToProfileWizardForInvite: () => void;
  arrivedViaUpdate: boolean;
  /** The welcome / private-invite / phone-verification modals to render. */
  interstitials: ReactNode;
}

/**
 * Owns the read-view "interstitial" experience shared by the legacy `TopicView`
 * and the component-driven `TopicReadPage` / `TopicCreateOrUpdatePage` shells: the welcome
 * sheet, the by-invitation-only detection + masked placeholder flow, and in-place
 * phone verification. Returns the state a shell needs to choose spinner /
 * placeholder / not-found / content, plus a ready-made `interstitials` node.
 *
 * The ref/effect semantics here are load-bearing (run-guard vs checked latch,
 * the stable `claimsKey` dep, the "topic loaded mid-check" guard) — a
 * by-invite recipient strands on an endless spinner if they drift. Kept verbatim.
 */
export function useTopicReadInterstitials({
  topicId,
  topic,
  loading,
  initialMode,
  onTopicBecameReadable,
  WelcomeComponent = DefaultWelcomeInterstitial,
}: UseTopicReadInterstitialsArgs): UseTopicReadInterstitials {
  const history = useHistory();
  const location = useLocation();
  const userContext = useUserContext();
  const { claims } = userContext;
  const { could } = useCasbin();
  const { isSuppressed: isNoticeSuppressed, loaded: noticesLoaded } =
    useSuppressibleNotices();
  const { isIOS } = useDeviceInfo();

  const senderName = useMemo(() => {
    const f = new URLSearchParams(location.search).get('f');
    return f ?? undefined;
  }, [location.search]);

  // The sharing leg the visitor arrived on, forwarded by `LegRedirect`. A type
  // whose welcome tells the story that traveled with the link (news) reads the
  // leg payload from it; every other type ignores it.
  const legId = useMemo(() => {
    const id = new URLSearchParams(location.search).get('legId');
    return id ?? undefined;
  }, [location.search]);

  // The update leg that brought the current user here, if any (#427). The
  // server derives it from the session's pending-invite claim (fresh /b/ link
  // arrivals, even anonymous) or the user's message history — no URL marker.
  // Drives the welcome copy, and the welcome sheet's "Keep the ball rolling &
  // reshare" passes the update on.
  const [receivedUpdate, setReceivedUpdate] =
    useState<ReceivedUpdateLike | null>(null);
  useEffect(() => {
    if (!topicId || initialMode !== 'view') return;
    let cancelled = false;
    MePrFoService.getReceivedUpdate(topicId)
      .then(received => {
        if (!cancelled) setReceivedUpdate(received);
      })
      .catch(() => {
        // Best-effort: absence of the signal just means default copy/buttons.
      });
    return () => {
      cancelled = true;
    };
  }, [topicId, initialMode]);
  const arrivedViaUpdate = receivedUpdate !== null;
  const receivedUpdateReshare = useMemo(
    () => reshareOfReceivedUpdate(receivedUpdate),
    [receivedUpdate]
  );

  const welcomeShownRef = useRef(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // Whether the topic page is the *currently routed* page. The topic can become
  // readable while the user is away on the Profile Wizard (verifying a phone there
  // flips their claims, which re-fetches this still-mounted page) — gate the
  // welcome sheet on this so it never pops up over the wizard; it fires on return.
  //
  // We track the route via history.listen rather than Ionic's view lifecycle:
  // TopicView lives in a nested IonRouterOutlet, and a top-level switch to
  // /profile does NOT propagate ionViewWillLeave/Enter down to it, so those hooks
  // are unreliable here. history.listen fires for every navigation regardless.
  const [currentPath, setCurrentPath] = useState(() => location.pathname);
  useEffect(
    () => history.listen(loc => setCurrentPath(loc.pathname)),
    [history]
  );
  const onTopicPage =
    !!topicId && currentPath === `/topics/${encodeURIComponent(topicId)}`;
  const [welcomeHideShareIntro, setWelcomeHideShareIntro] = useState(false);

  // Private-invite (by-invitation-only) state. A guest who followed a leg
  // invite link can't read the topic yet; instead of the not-found card we show
  // a masked placeholder + PrivateInviteInterstitial prompting them to link a
  // phone. invitePreviewData holds the minimal preview (type + image) the capability
  // endpoint returned; privateInviteShownRef remembers we went down this path so
  // we can show the trimmed WelcomeInterstitial variant once the topic loads.
  const [invitePreviewData, setInvitePreviewData] =
    useState<InvitePreview | null>(null);
  const [showPrivateInvite, setShowPrivateInvite] = useState(false);
  // When the recipient already has a phone on their profile, the private-invite
  // CTA verifies that phone in place (PhoneVerificationModal) instead of routing
  // through the Profile Wizard to link a new one.
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const privateInviteShownRef = useRef(false);
  const privateInviteCheckedRef = useRef(false);
  // Until private-invite detection resolves, render the spinner rather than the
  // not-found card — otherwise a by-invite recipient sees a 404 flash in the gap
  // between the topic fetch 404ing and could()/getInvitePreview() resolving.
  // Set true only once we've determined this is NOT a private invite.
  const [privateInviteResolved, setPrivateInviteResolved] = useState(false);

  // The detection effect below runs a single async check that must not be torn
  // down by an unrelated re-render. `claims` is an object whose identity churns
  // on load (the same reason CasbinContext serializes it), so depend on a stable
  // key and read the live claims via a ref inside the effect. Without this, a
  // claims-identity change after the guards pass re-runs the effect, whose
  // cleanup cancels the in-flight `could()` — and because privateInviteCheckedRef
  // is already latched, it never retries, leaving the guest stuck on "not found".
  const claimsKey = useMemo(
    () =>
      JSON.stringify({
        phone: claims.phoneVerifExp,
        email: claims.emailVerifExp,
      }),
    [claims.phoneVerifExp, claims.emailVerifExp]
  );
  const claimsRef = useRef(claims);
  claimsRef.current = claims;
  // Live topic ref so the in-flight detection can tell whether the topic loaded
  // while it was awaiting (don't mask an topic that became readable mid-check).
  const topicRef = useRef(topic);
  topicRef.current = topic;
  // Guards a single in-flight detection run. The effect re-runs whenever its deps
  // churn (loading/claims flip repeatedly during a slow /b/ → identify → reload),
  // so we must NOT cancel the in-flight check on every churn — that, plus the
  // checked latch, stranded by-invite recipients on an endless spinner. Instead a
  // run-guard lets the one run complete; `checked` is set only after it finishes.
  const privateInviteRunningRef = useRef(false);

  useEffect(() => {
    if (
      initialMode === 'view' &&
      topic !== null &&
      senderName !== undefined &&
      // Only when the topic page is the routed page — never over a pushed page.
      onTopicPage &&
      // A private-invite recipient already saw the "shared this" framing in the
      // PrivateInviteInterstitial; show them the trimmed variant below instead of
      // the full share intro (which would also race on top of the verify modal).
      !privateInviteShownRef.current &&
      !welcomeShownRef.current
    ) {
      welcomeShownRef.current = true;
      setWelcomeHideShareIntro(false);
      setShowWelcome(true);
    }
  }, [initialMode, topic, senderName, onTopicPage]);

  // Detect a by-invitation-only topic the guest can't read yet. When the fetch
  // resolved to no topic, ask whether linking a phone *could* unlock read
  // access (true only for the genuine pending invitee). If so, pull the masked
  // preview and open the PrivateInviteInterstitial. A plain 404 leaves
  // invitePreviewData null, so the normal not-found card renders.
  useEffect(() => {
    if (
      initialMode !== 'view' ||
      !topicId ||
      topic !== null ||
      loading ||
      privateInviteCheckedRef.current ||
      privateInviteRunningRef.current
    ) {
      return;
    }
    // Run once and let it COMPLETE regardless of re-renders — see
    // privateInviteRunningRef. No cancel-on-churn; state writes after unmount are
    // no-ops in React 18.
    privateInviteRunningRef.current = true;
    (async () => {
      try {
        // What (if anything) would unlock reading this topic? For this PR, any
        // upgrade the user doesn't already have means "link your phone to verify".
        const unlockLevel = await could(
          ACTIONS.READ,
          RESOURCES.TOPICS,
          topicId
        );
        const upgradeWouldUnlock =
          unlockLevel !== null &&
          unlockLevel !== CLAIMS_LEVEL.UNVERIFIED &&
          !ClaimsLogic.satisfies(claimsRef.current, unlockLevel);
        if (!upgradeWouldUnlock) {
          // Genuine not-found (or already-allowed) — let the not-found card render.
          privateInviteCheckedRef.current = true;
          setPrivateInviteResolved(true);
          return;
        }
        const preview = await MePrFoService.getInvitePreview(topicId);
        privateInviteCheckedRef.current = true;
        // The topic may have loaded while we were awaiting — don't mask it.
        if (topicRef.current !== null) return;
        if (!preview) {
          setPrivateInviteResolved(true);
          return;
        }
        privateInviteShownRef.current = true;
        setInvitePreviewData(preview);
        setShowPrivateInvite(true);
      } finally {
        privateInviteRunningRef.current = false;
      }
    })();
  }, [initialMode, topicId, topic, loading, could, claimsKey]);

  // On a successful load, clear the masked placeholder and re-arm detection so
  // that if the topic later becomes unreadable again — e.g. the user resets
  // their profile / lets their JWT expire while viewing it — the detection effect
  // re-runs instead of leaving privateInviteResolved stuck false (endless spinner).
  useEffect(() => {
    if (topic !== null) {
      setInvitePreviewData(null);
      setShowPrivateInvite(false);
      privateInviteCheckedRef.current = false;
      setPrivateInviteResolved(false);
    }
  }, [topic]);

  // Jump to the start of the Profile Wizard, dropping the `f` (sender) param so
  // the normal WelcomeInterstitial — which keys on `f` — doesn't fire when the
  // wizard returns here; the trimmed variant is shown instead.
  const goToProfileWizardForInvite = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('f');
    const qs = params.toString();
    const origin = qs ? `${location.pathname}?${qs}` : location.pathname;
    history.push(`/profile?origin=${encodeURIComponent(origin)}`);
  }, [history, location.pathname, location.search]);

  // After a private-invite recipient links their phone and the topic becomes
  // readable, show the trimmed WelcomeInterstitial variant (no share intro) —
  // but only if the home-install nudge isn't already suppressed (otherwise the
  // variant would be empty). On iOS the nudge is never shown (issue #475), so
  // the trimmed variant has nothing to display and is skipped entirely.
  useEffect(() => {
    if (
      initialMode === 'view' &&
      topic !== null &&
      privateInviteShownRef.current &&
      // Only fire once the topic page is the routed page again — the topic can
      // become readable while the user is away verifying a phone (in the Profile
      // Wizard or the in-place verify modal), and the welcome must not pop up over
      // either. Returning to the topic route flips onTopicPage true and this runs.
      onTopicPage &&
      !showPhoneVerification &&
      !welcomeShownRef.current &&
      noticesLoaded &&
      !isIOS &&
      !isNoticeSuppressed('homeInstall')
    ) {
      welcomeShownRef.current = true;
      setWelcomeHideShareIntro(true);
      setShowWelcome(true);
    }
  }, [
    initialMode,
    topic,
    onTopicPage,
    showPhoneVerification,
    noticesLoaded,
    isIOS,
    isNoticeSuppressed,
  ]);

  const topicTypeLabel =
    TOPIC_TYPES.find(t => t.id === topic?.type)?.messageSubstitution ?? 'topic';

  const interstitials = (
    <>
      <WelcomeComponent
        isOpen={showWelcome}
        topicType={topicTypeLabel}
        senderName={senderName}
        hideShareIntro={welcomeHideShareIntro}
        isUpdate={arrivedViaUpdate}
        topic={topic}
        topicId={topicId}
        legId={legId}
        receivedUpdateReshare={receivedUpdateReshare}
        onDismiss={() => setShowWelcome(false)}
      />

      <PrivateInviteInterstitial
        isOpen={showPrivateInvite}
        topicType={
          TOPIC_TYPES.find(t => t.id === invitePreviewData?.type)
            ?.messageSubstitution ?? 'topic'
        }
        senderName={senderName}
        hasPhone={userContext.phone !== undefined}
        onLinkPhone={() => {
          setShowPrivateInvite(false);
          // Already has a phone → verify it in place; otherwise go link one.
          if (userContext.phone !== undefined) {
            setShowPhoneVerification(true);
          } else {
            goToProfileWizardForInvite();
          }
        }}
        onDismiss={() => setShowPrivateInvite(false)}
      />

      <PhoneVerificationModal
        isOpen={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        continuePath={
          topicId
            ? `/topics/${encodeURIComponent(topicId)}`
            : location.pathname || '/'
        }
        onSuccess={onTopicBecameReadable}
      />
    </>
  );

  return {
    invitePreviewData,
    privateInviteResolved,
    goToProfileWizardForInvite,
    arrivedViaUpdate,
    interstitials,
  };
}
